import React, { useCallback, useContext } from 'react';
import { Route, Routes as RoutesDOM } from 'react-router-dom';
import NotFound from '@pages/All/NotFound';
import SessionContext from '@providers/Session/SessionContext';
import Main from '@pages/main';
import Home from '@pages/All/Home';
import { ApplicationCategoriesProps } from '@config/useApps';
import PortalSpinner from '@components/PortalSpinner';
import { RouteItemProps } from '@config/routes/types';

const Routes = () => {
  const { isRoot, allowedPagesRouting } = useContext(SessionContext);

  const hasMoreThanOneCategory = useCallback(
    (categories: ApplicationCategoriesProps[]) => {
      return categories && categories?.length > 1;
    },
    [],
  );

  const handlePages = useCallback(
    (pages: RouteItemProps[], categoryName?: string, hasLorawan?: boolean) =>
      pages?.flatMap(
        ({
          path,
          element,
          externalLink,
          rootCannotSee,
          isLoraWan,
          isCommon,
        }) => {
          const rootCannotSeeThisPage = rootCannotSee && isRoot;
          if (rootCannotSeeThisPage || externalLink) return undefined;
          let treatedPath = path;
          if (categoryName) {
            if (hasLorawan) {
              const deviceType = isLoraWan ? 'lorawan' : 'mobile-network';
              treatedPath = `${deviceType}/${categoryName}/${path}`;
              if (isCommon) {
                const mobileNetworkTreatedPath = `mobile-network/${categoryName}/${path}`;
                const lorawanTreatedPath = `lorawan/${categoryName}/${path}`;

                return [
                  <Route
                    key={mobileNetworkTreatedPath}
                    path={mobileNetworkTreatedPath}
                    element={element}
                  />,
                  <Route
                    key={lorawanTreatedPath}
                    path={lorawanTreatedPath}
                    element={element}
                  />,
                ];
              }
            } else {
              treatedPath = `${categoryName}/${path}`;
            }
          }

          return (
            <Route key={treatedPath} path={treatedPath} element={element} />
          );
        },
      ),
    [isRoot],
  );

  if (!allowedPagesRouting || allowedPagesRouting?.length === 0) {
    return <PortalSpinner />;
  }

  return (
    <RoutesDOM>
      <Route path="/*" element={<Main />}>
        <Route index element={<Home />} />
        {allowedPagesRouting?.map(({ name, categories, hasLorawan }) => (
          <Route key={name} path={`${name}/*`}>
            {hasMoreThanOneCategory(categories)
              ? categories.map(({ categoryName, pages }) =>
                  handlePages(pages, categoryName, hasLorawan),
                )
              : handlePages(
                  categories?.[0]?.pages,
                  categories?.[0]?.categoryName !== name
                    ? categories?.[0]?.categoryName
                    : undefined,
                  hasLorawan,
                )}
            <Route path="*" element={<NotFound />} />
          </Route>
        ))}
        <Route path="*" element={<NotFound />} />
      </Route>
    </RoutesDOM>
  );
};

export default Routes;
